import {AxiosError} from "axios";
import {Bin as DeleteIcon, Dns as DomainsIcon, Plus as AddMapIcon} from "iconoir-react";
import {useEffect, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {MTAApi, PostfixMap} from "../api/admin/generated";
import {ActionsDropdown} from "../common/ActionsDropdown";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {DialogCloseCause} from "../common/Enums";
import {InvertSelection} from "../common/InvertSelection";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {Truncate} from "../common/Truncate";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {MTAMapPopup} from "./MTAMapPopup";

/*
 * REST api for accessing the back-end
 */
const mtaApi = new MTAApi(ApiConfig.config());

type OnClick = () => void;

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
    onClick? : OnClick
}

const Column = ({value, children, className, maxLength = 60, onClick}: ColumnProps) => {
    return (
        <td onClick={onClick} className={"cursor-pointer " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const MTAMaps = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The maps
     */
    const [maps, setMaps] = useState<PostfixMap[]>([]);
    /**
     * The selected maps
     */
    const [selectedMaps, setSelectedMaps] = useState<Array<PostfixMap>>([]);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The clicked map item
     */
    const [clickedMap, setClickedMap] = useState<PostfixMap>();
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "edit map dialog" modal dialog will be shown
     */
    const [showEditMapDialog, setShowEditMapDialog] = useState<boolean>(false);
    /**
     * If true, the "add map dialog" modal dialog will be shown
     */
    const [showAddMapDialog, setShowAddMapDialog] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        logger.debug("Load maps")

        // reset state when new data is loaded
        setErrorMessage(undefined);
        setSelectedMaps([]);
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerListMaps()
            .then(response => {
                setMaps(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting maps", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    const editMap = (map : PostfixMap) =>
    {
        setClickedMap(map);
        setShowEditMapDialog(true);
    }

    /**
     * Is called when the EditMapDialog is closed
     */
    const onEditMapDialogClose = () => {
        setShowEditMapDialog(false);
    }

    /**
     * Is called when the EditMapDialog is closed
     */
    const onAddMapDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowAddMapDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    const deleteSelectedMaps = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        for (const map of selectedMaps)
        {
            mtaApi.mTAControllerDeleteMap(map.type, map.name)
                .then(() => {
                    setForceReload(true);
                })
                .catch((err: Error | AxiosError) => {
                    logger.error("Error deleting map", err);
                    Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                    setErrorMessage(Utils.getErrorMessage(err, t));
                })
                .finally(() => {
                    if (setSpinnerStateCounter !== undefined) {
                        setSpinnerStateCounter(count => count - 1);
                    }
                    setShowDeleteConfirm(false);
                    setClickedMap(undefined);
                })
        }
    }

    const invertSelection = () => {
        setSelectedMaps([...maps].filter(m => !selectedMaps.includes(m)));
    }

    /**
     * Called when a map is selected (i.e., when the checkbox is checked)
     */
    const onSelectMap = (checked : boolean, map : PostfixMap) =>
    {
        setSelectedMaps(checked ?
            [...selectedMaps, map] :
            selectedMaps.filter(m => m.type !== map.type || m.name !== map.name)
        );
    }

    return (
        <>
            <ConfirmDialog title={t("mta-maps.confirm")}
                           bodyText={t("mta-maps.body-text")}
                           confirmButtonTitle={t("mta-maps.delete")}
                           onClickConfirmEvent={deleteSelectedMaps}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showAddMapDialog && <MTAMapPopup onClose={onAddMapDialogClose} />}
            {showEditMapDialog && clickedMap !== undefined && <MTAMapPopup editType={clickedMap.type} editName={clickedMap.name} onClose={onEditMapDialogClose} />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("mta-maps.title")} icon={<DomainsIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <ActionsDropdown>
                            <Dropdown.Item onClick={() => setShowAddMapDialog(true)}>
                                <AddMapIcon className="text-success"/> {t("mta-maps.add-map")}
                            </Dropdown.Item>
                        </ActionsDropdown>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        <thead className="sticky-top">
                        <tr className="text-nowrap">
                            {selectedMaps.length === 0 ?
                                <>
                                    <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                    <th>{t("mta-maps.name")}</th>
                                    <th>{t("mta-maps.type")}</th>
                                    <th>{t("mta-maps.filename")}</th>
                                </>
                                :
                                <th colSpan={4}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedMaps([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedMaps.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                <DeleteIcon className="text-danger"/> {t("mta-maps.delete-map")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            }
                        </tr>
                        </thead>
                        <tbody>
                        {maps.map((m: PostfixMap) => (
                            <tr key={m.filename} className="text-nowrap">
                                <Column className="td-w-min align-middle">
                                    <Form.Check
                                        type="checkbox"
                                        className="me-0"
                                        checked={selectedMaps.find((smap) =>  smap.type === m.type && smap.name == m.name) !== undefined}
                                        onChange={(e) => onSelectMap(e.target.checked, m)}
                                        inline
                                    />
                                </Column>
                                <Column value={m.name} onClick={() => editMap(m)}/>
                                <Column value={m.type} onClick={() => editMap(m)}/>
                                <Column value={m.filename + ".map"} onClick={() => editMap(m)}/>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </>
    );
}
